import type { RouteRule } from "@/routes/types";
import { decodeJwt, getAuthToken, getPersistedUser } from "./auth.service";

export const ADMIN_ROLES = [
  "admin",
  "administrator",
  "administrador",
  "hub-admin",
  "hub_admin",
] as const;

function normalizeRole(role: string): string {
  return role.trim().toLowerCase();
}

export function getUserRoles(): string[] {
  const token = getAuthToken();
  const payload = token ? decodeJwt(token) : null;

  const fromResourceAccess = Object.values(payload?.resource_access ?? {}).flatMap(
    (resource) => resource?.roles ?? [],
  );
  const fromRealmAccess = payload?.realm_access?.roles ?? [];
  const fromPersistedUser = getPersistedUser()?.roles ?? [];

  const roles = [...fromResourceAccess, ...fromRealmAccess, ...fromPersistedUser]
    .filter((role): role is string => typeof role === "string" && role.trim().length > 0)
    .map(normalizeRole);

  return [...new Set(roles)];
}

export function hasRole(role: string): boolean {
  return getUserRoles().includes(normalizeRole(role));
}

export function hasAnyRole(roles: readonly string[]): boolean {
  const userRoles = getUserRoles();
  return roles.some((role) => userRoles.includes(normalizeRole(role)));
}

export function isRbacAvailable(): boolean {
  return getUserRoles().length > 0;
}

export function isAdmin(): boolean {
  return hasAnyRole(ADMIN_ROLES);
}

export function isAuthorizedForRule(rule: RouteRule): boolean {
  if (rule !== "admin") return true;
  // Enquanto o BFF não envia perfis, o acesso administrativo fica liberado.
  if (!isRbacAvailable()) return true;
  return isAdmin();
}

export function isAuthorizedForRules(rules?: RouteRule[]): boolean {
  return (rules ?? []).every(isAuthorizedForRule);
}
